(() => {
  const SELECTORS = {
    tabs: '[data-yimi-collection-tabs]',
    tab: '[role="tab"]',
    panel: '[role="tabpanel"]',
    slider: '.yimi-new-figures__grid.slider',
    header: '[data-menu-open-trigger]',
  };

  function resetPanelSliders(panel) {
    requestAnimationFrame(() => {
      panel?.querySelectorAll('slider-component').forEach((slider) => slider.resetPages?.());
      window.dispatchEvent(new Event('resize'));
    });
  }

  function activateTab(tabs, tab, focus = false) {
    if (!tabs || !tab) return;

    tabs.querySelectorAll(SELECTORS.tab).forEach((item) => {
      const isActive = item === tab;
      item.setAttribute('aria-selected', String(isActive));
      item.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    const panelId = tab.getAttribute('aria-controls');
    tabs.querySelectorAll(SELECTORS.panel).forEach((panel) => {
      panel.hidden = panel.id !== panelId;
    });

    if (focus) tab.focus();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    tab.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
    resetPanelSliders(document.getElementById(panelId));
  }

  function enableMouseDrag(slider) {
    if (slider.dataset.yimiDragReady === 'true') return;
    slider.dataset.yimiDragReady = 'true';

    let pointerId;
    let startX = 0;
    let startScroll = 0;
    let dragged = false;
    let suppressClick = false;

    slider.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = slider.scrollLeft;
      dragged = false;
      suppressClick = false;
    });

    slider.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const distance = event.clientX - startX;
      if (Math.abs(distance) > 4) {
        dragged = true;
        slider.classList.add('is-dragging');
        if (!slider.hasPointerCapture(pointerId)) slider.setPointerCapture(pointerId);
      }
      if (dragged) slider.scrollLeft = startScroll - distance;
    });

    const stopDragging = (event) => {
      if (event.pointerId !== pointerId) return;
      if (slider.hasPointerCapture(pointerId)) slider.releasePointerCapture(pointerId);
      suppressClick = dragged;
      pointerId = undefined;
      slider.classList.remove('is-dragging');
      window.setTimeout(() => {
        dragged = false;
        suppressClick = false;
      }, 0);
    };

    slider.addEventListener('pointerup', stopDragging);
    slider.addEventListener('pointercancel', stopDragging);
    slider.addEventListener('dragstart', (event) => event.preventDefault());
    slider.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);
  }

  function initCollectionTabs(root) {
    root.querySelectorAll(SELECTORS.tabs).forEach((tabs) => {
      if (tabs.dataset.yimiTabsReady === 'true') return;
      tabs.dataset.yimiTabsReady = 'true';

      tabs.querySelectorAll(SELECTORS.slider).forEach(enableMouseDrag);
      tabs.querySelectorAll(SELECTORS.tab).forEach((tab) => {
        tab.addEventListener('click', () => activateTab(tabs, tab));
        tab.addEventListener('keydown', (event) => {
          const tabList = Array.from(tabs.querySelectorAll(SELECTORS.tab));
          const currentIndex = tabList.indexOf(tab);
          let nextIndex = currentIndex;

          if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabList.length;
          if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabList.length) % tabList.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabList.length - 1;
          if (nextIndex === currentIndex) return;

          event.preventDefault();
          activateTab(tabs, tabList[nextIndex], true);
        });
      });
    });
  }

  function initHoverMenus(root) {
    root.querySelectorAll(SELECTORS.header).forEach((wrapper) => {
      if (wrapper.dataset.yimiHeaderReady === 'true') return;
      wrapper.dataset.yimiHeaderReady = 'true';
      if (wrapper.dataset.menuOpenTrigger !== 'hover') return;

      const desktopPointer = window.matchMedia('(min-width: 990px) and (hover: hover)');
      wrapper.querySelectorAll('header-menu').forEach((menu) => {
        const details = menu.querySelector(':scope > details');
        const summary = details?.querySelector(':scope > summary');
        if (!details || !summary) return;

        let closeTimer;
        menu.addEventListener('pointerenter', () => {
          if (!desktopPointer.matches) return;
          window.clearTimeout(closeTimer);
          wrapper.querySelectorAll('header-menu > details[open]').forEach((openDetails) => {
            if (openDetails === details) return;
            openDetails.removeAttribute('open');
            openDetails.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
          });
          details.setAttribute('open', '');
          summary.setAttribute('aria-expanded', 'true');
        });

        menu.addEventListener('pointerleave', () => {
          if (!desktopPointer.matches) return;
          closeTimer = window.setTimeout(() => {
            if (menu.contains(document.activeElement)) return;
            details.removeAttribute('open');
            summary.setAttribute('aria-expanded', 'false');
          }, 120);
        });
      });
    });
  }

  function init(root = document) {
    initCollectionTabs(root);
    initHoverMenus(root);
  }

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const panel = event.target.closest?.(SELECTORS.panel);
    const tabs = panel?.closest(SELECTORS.tabs);
    const tab = panel && tabs?.querySelector(`[aria-controls="${panel.id}"]`);
    if (tab) activateTab(tabs, tab);
  });
})();
